import { useRef, useState, useEffect, useCallback, RefObject } from 'react';

interface UseDragToSnapOptions {
    slidesLength: number;
    activeIndex: number;
    setActiveIndex: (index: number | ((prev: number) => number)) => void;
    isTransitioning: boolean;
    setIsTransitioning: (value: boolean) => void;
    setIsPaused: (value: boolean) => void;
    swipeThreshold?: number;
    clickThreshold?: number;
    clickTimeThreshold?: number;
    transitionDelay?: number;
    onPrev?: () => void;
    onNext?: () => void;
}

interface UseDragToSnapReturn {
    containerRef: RefObject<HTMLDivElement | null>;
    isDragging: boolean;
    dragOffsetPx: number;
    wasSwipe: boolean;
}

export const useDragToSnap = (options: UseDragToSnapOptions): UseDragToSnapReturn => {
    const {
        slidesLength,
        activeIndex,
        setActiveIndex,
        isTransitioning,
        setIsTransitioning,
        setIsPaused,
        swipeThreshold = 50,
        clickThreshold = 5,
        clickTimeThreshold = 200,
        transitionDelay = 300,
        onPrev,
        onNext,
    } = options;

    const containerRef = useRef<HTMLDivElement>(null);
    const startXRef = useRef<number | null>(null);
    const startYRef = useRef<number | null>(null);
    const deltaXRef = useRef<number>(0);
    const swipeLockRef = useRef<boolean>(false);
    const clickStartTimeRef = useRef<number>(0);
    const lastTransitionTimeRef = useRef<number>(0);

    const [isDragging, setIsDragging] = useState(false);
    const [dragOffsetPx, setDragOffsetPx] = useState(0);
    const [wasSwipe, setWasSwipe] = useState(false);

    const goPrev = useCallback(() => {
        const now = Date.now();
        if (isTransitioning || (now - lastTransitionTimeRef.current) < transitionDelay) return;

        lastTransitionTimeRef.current = now;
        setIsTransitioning(true);
        
        if (onPrev) {
            onPrev();
        } else {
            setActiveIndex((i) => (i === 0 ? slidesLength - 1 : i - 1));
        }
        
        setTimeout(() => setIsTransitioning(false), transitionDelay);
    }, [isTransitioning, slidesLength, transitionDelay, setIsTransitioning, setActiveIndex, onPrev]);

    const goNext = useCallback(() => {
        const now = Date.now();
        if (isTransitioning || (now - lastTransitionTimeRef.current) < transitionDelay) return;

        lastTransitionTimeRef.current = now;
        setIsTransitioning(true);
        
        if (onNext) {
            onNext();
        } else {
            setActiveIndex((i) => (i === slidesLength - 1 ? 0 : i + 1));
        }
        
        setTimeout(() => setIsTransitioning(false), transitionDelay);
    }, [isTransitioning, slidesLength, transitionDelay, setIsTransitioning, setActiveIndex, onNext]);

    // Touch handlers
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) return;
            const t = e.touches[0];
            startXRef.current = t.clientX;
            startYRef.current = t.clientY;
            deltaXRef.current = 0;
            swipeLockRef.current = false;
            clickStartTimeRef.current = Date.now();
            setIsDragging(true);
            setIsPaused(true);
        };

        const onTouchMove = (e: TouchEvent) => {
            if (startXRef.current == null || startYRef.current == null) return;
            const t = e.touches[0];
            const dx = t.clientX - startXRef.current;
            const dy = Math.abs(t.clientY - startYRef.current);
            deltaXRef.current = dx;

            if (!swipeLockRef.current && Math.abs(dx) > dy && Math.abs(dx) > 10) {
                swipeLockRef.current = true;
                e.preventDefault();
            }

            if (swipeLockRef.current) {
                setDragOffsetPx(dx);
            }
        };

        const onTouchEnd = () => {
            const dx = deltaXRef.current;
            const totalMovement = Math.abs(dx);
            const clickDuration = Date.now() - clickStartTimeRef.current;

            const isClick = totalMovement <= clickThreshold && clickDuration <= clickTimeThreshold;

            if (Math.abs(dx) >= swipeThreshold && !isTransitioning && !isClick) {
                setWasSwipe(true);
                if (dx < 0) {
                    goNext();
                } else {
                    goPrev();
                }
            } else {
                setWasSwipe(false);
            }

            startXRef.current = null;
            startYRef.current = null;
            deltaXRef.current = 0;
            swipeLockRef.current = false;
            setIsDragging(false);
            setDragOffsetPx(0);
            
            const resumeId = window.setTimeout(() => setIsPaused(false), 300);
            const swipeResetId = window.setTimeout(() => setWasSwipe(false), 100);
            
            return () => {
                window.clearTimeout(resumeId);
                window.clearTimeout(swipeResetId);
            };
        };

        el.addEventListener("touchstart", onTouchStart, { passive: false });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd);
        el.addEventListener("touchcancel", onTouchEnd);

        return () => {
            el.removeEventListener("touchstart", onTouchStart as EventListener);
            el.removeEventListener("touchmove", onTouchMove as EventListener);
            el.removeEventListener("touchend", onTouchEnd as EventListener);
            el.removeEventListener("touchcancel", onTouchEnd as EventListener);
        };
    }, [slidesLength, isTransitioning, swipeThreshold, clickThreshold, clickTimeThreshold, setIsPaused, goPrev, goNext]);

    // Mouse handlers
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;

            startXRef.current = e.clientX;
            startYRef.current = e.clientY;
            deltaXRef.current = 0;
            swipeLockRef.current = false;
            clickStartTimeRef.current = Date.now();
            setIsDragging(true);
            setIsPaused(true);

            // Меняем курсор и предотвращаем выделение
            e.preventDefault();
            el.style.cursor = 'grabbing';
        };

        const onMouseMove = (e: MouseEvent) => {
            if (startXRef.current == null || startYRef.current == null) return;

            const dx = e.clientX - startXRef.current;
            const dy = Math.abs(e.clientY - startYRef.current);
            deltaXRef.current = dx;

            if (!swipeLockRef.current && Math.abs(dx) > dy && Math.abs(dx) > 10) {
                swipeLockRef.current = true;
                e.preventDefault();
            }

            if (swipeLockRef.current) {
                setDragOffsetPx(dx);
            }
        };

        const onMouseUp = () => {
            const dx = deltaXRef.current;
            const totalMovement = Math.abs(dx);
            const clickDuration = Date.now() - clickStartTimeRef.current;

            const isClick = totalMovement <= clickThreshold && clickDuration <= clickTimeThreshold;

            if (Math.abs(dx) >= swipeThreshold && !isTransitioning && !isClick) {
                setWasSwipe(true);
                if (dx < 0) {
                    goNext();
                } else {
                    goPrev();
                }
            } else {
                setWasSwipe(false);
            }

            startXRef.current = null;
            startYRef.current = null;
            deltaXRef.current = 0;
            swipeLockRef.current = false;
            setIsDragging(false);
            setDragOffsetPx(0);
            
            // Возвращаем курсор
            el.style.cursor = 'grab';
            
            const resumeId = window.setTimeout(() => setIsPaused(false), 300);
            const swipeResetId = window.setTimeout(() => setWasSwipe(false), 100);
            
            return () => {
                window.clearTimeout(resumeId);
                window.clearTimeout(swipeResetId);
            };
        };

        const onMouseLeave = () => {
            startXRef.current = null;
            startYRef.current = null;
            deltaXRef.current = 0;
            swipeLockRef.current = false;
            setIsDragging(false);
            setDragOffsetPx(0);
            setIsPaused(false);
            setWasSwipe(false);
            
            // Возвращаем курсор
            el.style.cursor = 'grab';
        };

        // Устанавливаем курсор grab при монтировании
        el.style.cursor = 'grab';

        el.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        el.addEventListener("mouseleave", onMouseLeave);

        return () => {
            el.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            el.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [slidesLength, isTransitioning, swipeThreshold, clickThreshold, clickTimeThreshold, setIsPaused, goNext, goPrev]);

    return {
        containerRef,
        isDragging,
        dragOffsetPx,
        wasSwipe,
    };
};

